import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { caissesAPI, type CaisseVirementInfo } from '../../api/caisses'

const BANQUES_TN = [
  'STB', 'BNA', 'BH', 'Attijari', 'UIB',
  'BIAT', 'Amen Bank', 'Arab Tunisian Bank', 'Autre',
]

interface Props {
  caisseId: string
  current?: CaisseVirementInfo
  onClose: () => void
  onSuccess: () => void
}

export function BankInfoModal({ caisseId, current, onClose, onSuccess }: Props) {
  const [rib, setRib]               = useState(current?.rib ?? '')
  const [banque, setBanque]         = useState(current?.banque ?? '')
  const [nom, setNom]               = useState(current?.nomTitulaire ?? '')
  const [ribError, setRibError]     = useState('')

  const mutation = useMutation({
    mutationFn: () => caissesAPI.saveBankInfo(caisseId, {
      rib:          rib.replace(/\s/g, ''),
      banque,
      nomTitulaire: nom,
    }),
    onSuccess,
  })

  function validateRib(value: string) {
    const clean = value.replace(/\s/g, '')
    if (clean.length > 0 && !/^[0-9]+$/.test(clean)) {
      setRibError('Le RIB ne doit contenir que des chiffres')
    } else if (clean.length === 20 || clean.length === 0) {
      setRibError('')
    } else {
      setRibError(`${clean.length}/20 chiffres`)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const clean = rib.replace(/\s/g, '')
    if (!/^[0-9]{20}$/.test(clean)) {
      setRibError('20 chiffres requis')
      return
    }
    if (!banque || !nom) return
    mutation.mutate()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28,
        width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,.18)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--navy-900)' }}>
            Infos bancaires
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* RIB */}
          <div>
            <label style={{ display: 'block', fontSize: '.82rem', color: 'var(--muted)', marginBottom: 4 }}>
              RIB (20 chiffres)
            </label>
            <input
              type="text"
              value={rib}
              onChange={e => { setRib(e.target.value); validateRib(e.target.value) }}
              placeholder="01 234 5678901234567890"
              maxLength={24}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: '.9rem',
                border: `1px solid ${ribError ? '#e74c3c' : 'var(--line)'}`,
                boxSizing: 'border-box',
              }}
            />
            {ribError && <p style={{ margin: '4px 0 0', fontSize: '.78rem', color: '#e74c3c' }}>{ribError}</p>}
          </div>

          {/* Banque */}
          <div>
            <label style={{ display: 'block', fontSize: '.82rem', color: 'var(--muted)', marginBottom: 4 }}>
              Banque
            </label>
            <select
              value={banque}
              onChange={e => setBanque(e.target.value)}
              required
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: '.9rem',
                border: '1px solid var(--line)', boxSizing: 'border-box',
                background: '#fff',
              }}
            >
              <option value="">— Sélectionner —</option>
              {BANQUES_TN.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          {/* Nom titulaire */}
          <div>
            <label style={{ display: 'block', fontSize: '.82rem', color: 'var(--muted)', marginBottom: 4 }}>
              Nom du titulaire
            </label>
            <input
              type="text"
              value={nom}
              onChange={e => setNom(e.target.value)}
              placeholder="Prénom NOM"
              required
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: '.9rem',
                border: '1px solid var(--line)', boxSizing: 'border-box',
              }}
            />
          </div>

          {mutation.isError && (
            <p style={{ margin: 0, fontSize: '.83rem', color: '#e74c3c' }}>
              Erreur lors de la sauvegarde. Réessayez.
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              style={{ flex: 1 }}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
